import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { whatsappService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/WhatsAppConfig.css';

const WhatsAppConfig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('loading');
  const [qrCode, setQrCode] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restarting, setRestarting] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [diagnostics, setDiagnostics] = useState({});
  const [clearingSession, setClearingSession] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const loadData = async () => {
    try {
      await Promise.all([loadStatus(), loadTemplates()]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const data = await whatsappService.getStatus();
      console.log('[WhatsAppConfig] Status recebido:', data);
      console.log('[WhatsAppConfig] QR Code:', data.qrCode ? 'Presente (' + data.qrCode.length + ' chars)' : 'Ausente');
      setStatus(data.status);
      setQrCode(data.qrCode || null);
      setPhoneNumber(data.status === 'connected' ? data.phoneNumber : null);
      setErrorMessage(data.errorMessage);
      setDiagnostics({
        requiresQr: !!data.requiresQr,
        authenticated: !!data.authenticated,
        reconnectAttempts: data.reconnectAttempts ?? 0,
        nextReconnectAt: data.nextReconnectAt,
        lastDisconnectedAt: data.lastDisconnectedAt,
        lastErrorAt: data.lastErrorAt,
        disconnectReason: data.disconnectReason,
        lastReconnectReason: data.lastReconnectReason,
        reconnectInProgress: !!data.reconnectInProgress,
        connectingSince: data.connectingSince,
        lastConnectedAt: data.lastConnectedAt
      });
    } catch (error) {
      console.error('Erro ao obter status:', error);
      setStatus('error');
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await whatsappService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleInitialize = async () => {
    try {
      setInitializing(true);
      setMessage({ type: '', text: '' });
      const result = await whatsappService.initialize();
      if (result.status === 'initialized' || result.status === 'already_initialized') {
        setMessage({ type: 'success', text: 'WhatsApp inicializado! Aguarde o QR Code.' });
        await loadStatus();
      } else {
        setMessage({ type: 'error', text: 'Erro ao inicializar WhatsApp' });
      }
    } catch (error) {
      console.error('Erro ao inicializar:', error);
      setMessage({ type: 'error', text: 'Erro ao inicializar WhatsApp: ' + error.message });
    } finally {
      setInitializing(false);
    }
  };

  const handleClearSession = async () => {
    if (!window.confirm('Isso apaga a sessao salva no servidor e gera um NOVO QR Code. Continuar?')) {
      return;
    }
    try {
      setClearingSession(true);
      setMessage({ type: '', text: '' });
      await whatsappService.clearSession();
      setMessage({ type: 'success', text: 'Sessao removida. Escaneie o novo QR Code.' });
      setStatus('connecting');
      setQrCode(null);
      await loadStatus();
    } catch (error) {
      console.error('Erro ao limpar sessao:', error);
      setMessage({ type: 'error', text: 'Erro ao limpar sessao: ' + (error.response?.data?.error || error.message) });
    } finally {
      setClearingSession(false);
    }
  };

  const handleRestart = async () => {
    try {
      setRestarting(true);
      setMessage({ type: '', text: '' });
      const result = await whatsappService.restart();
      if (result.status === 'initialized' || result.status === 'already_initialized') {
        setMessage({ type: 'success', text: 'WhatsApp reiniciado! Aguarde o QR Code.' });
        setStatus('connecting');
        setQrCode(null);
        await loadStatus();
      } else {
        setMessage({ type: 'error', text: 'Erro ao reiniciar WhatsApp' });
      }
    } catch (error) {
      console.error('Erro ao reiniciar:', error);
      setMessage({ type: 'error', text: 'Erro ao reiniciar WhatsApp: ' + error.message });
    } finally {
      setRestarting(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      template: template.template || ''
    });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;

    try {
      setMessage({ type: '', text: '' });
      await whatsappService.updateTemplate(editingTemplate.id, {
        name: editingTemplate.name,
        template: editingTemplate.template,
        is_active: editingTemplate.is_active
      });
      setMessage({ type: 'success', text: 'Template atualizado com sucesso!' });
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar template: ' + error.message });
    }
  };

  const handleSendTest = async () => {
    if (!testPhone || !testMessage) {
      setMessage({ type: 'error', text: 'Preencha o telefone e a mensagem de teste' });
      return;
    }

    try {
      setSendingTest(true);
      setMessage({ type: '', text: '' });
      await whatsappService.sendTest(testPhone, testMessage);
      setMessage({ type: 'success', text: 'Mensagem de teste enviada com sucesso!' });
      setTestPhone('');
      setTestMessage('');
    } catch (error) {
      console.error('Erro ao enviar teste:', error);
      setMessage({ type: 'error', text: 'Erro ao enviar mensagem: ' + error.message });
    } finally {
      setSendingTest(false);
    }
  };

  const getEventTypeName = (eventType) => {
    const names = {
      'venda_status_change': 'Mudanca de Status da Venda',
      'venda_voltou_fila': 'Venda Voltou a Fila',
      'venda_sem_atribuicao': 'Venda Sem Atribuicao',
      'venda_cpf_com_cnpj_disponivel': 'Venda CPF com CNPJ disponível',
      'did_chips_liberados': 'DID - Chips liberados',
      'did_distribuido': 'DID - Chips atribuídos',
      'did_comprovante_enviado': 'DID - Comprovante enviado',
      'did_pagamento_confirmado': 'DID - Pagamento confirmado',
      'did_troca_senha_pedida': 'DID - Troca de senha solicitada',
      'did_senha_atualizada': 'DID - Senha atualizada',
      'did_expirado': 'DID - Chip(s) expirados'
    };
    return names[eventType] || eventType;
  };

  const getAvailableVariables = (eventType) => {
    const vars = {
      'venda_status_change': ['vendedor_nome', 'cliente_nome', 'status_anterior', 'status_novo', 'data', 'horario'],
      'venda_voltou_fila': ['venda_id', 'cliente_nome', 'status', 'backoffice_nome', 'data', 'horario'],
      'venda_sem_atribuicao': ['venda_id', 'cliente_nome', 'cliente_telefone', 'status', 'data', 'horario'],
      'venda_cpf_com_cnpj_disponivel': ['venda_id', 'cliente_nome', 'cpf', 'cnpjs_lista', 'vendedor_nome', 'data', 'horario'],
      'did_chips_liberados': ['usuario_nome', 'quantidade', 'modo', 'validade', 'data', 'horario'],
      'did_distribuido': ['vendedor_nome', 'supervisor_nome', 'quantidade', 'validade', 'data', 'horario'],
      'did_comprovante_enviado': ['ordem_id', 'solicitante_nome', 'solicitante_role', 'quantidade', 'valor_total', 'data', 'horario'],
      'did_pagamento_confirmado': ['solicitante_nome', 'ordem_id', 'quantidade', 'valor_total', 'data', 'horario'],
      'did_troca_senha_pedida': ['dono_nome', 'usuario_chip', 'data', 'horario'],
      'did_senha_atualizada': ['dono_nome', 'usuario_chip', 'data', 'horario'],
      'did_expirado': ['usuario_nome', 'quantidade', 'data', 'horario']
    };
    return vars[eventType] || [];
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'connected':
        return <span className="status-badge status-connected">Conectado</span>;
      case 'connecting':
        return <span className="status-badge status-connecting">Conectando...</span>;
      case 'error':
        return <span className="status-badge status-error">Erro</span>;
      case 'disconnected':
      default:
        return <span className="status-badge status-disconnected">Desconectado</span>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Carregando...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="whatsapp-config">
        <div className="whatsapp-header">
          <div>
            <h1 className="card-title">📱 Configuracao do WhatsApp</h1>
            <p className="subtitle">Gerencie a conexao e templates de mensagens do WhatsApp</p>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              🏠 Voltar ao Dashboard
            </button>
          </div>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Status da Conexao */}
        <div className="config-card">
          <h2>Status da Conexao</h2>
          <div className="status-container">
            <div className="status-info">
              {getStatusBadge()}
              {status === 'connected' && phoneNumber && (
                <span className="phone-number">📱 {phoneNumber}</span>
              )}
            </div>
            {errorMessage && <p className="error-message">{errorMessage}</p>}
            {diagnostics.requiresQr && (
              <p className="warning" style={{ marginTop: '0.75rem' }}>
                E necessario escanear o QR Code novamente (falha de autenticacao). Use &quot;Limpar sessao&quot; se o QR nao aparecer.
              </p>
            )}
          </div>

          {(diagnostics.reconnectAttempts > 0 ||
            diagnostics.nextReconnectAt ||
            diagnostics.disconnectReason ||
            diagnostics.reconnectInProgress) && (
            <div className="whatsapp-diagnostics" style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.04)', borderRadius: '8px', fontSize: '0.9rem' }}>
              <strong>Diagnostico (reconexao automatica)</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                {diagnostics.reconnectInProgress && <li>Reconexao em andamento...</li>}
                <li>Tentativas de reconexao: {diagnostics.reconnectAttempts}</li>
                {diagnostics.nextReconnectAt && (
                  <li>Proxima tentativa: {new Date(diagnostics.nextReconnectAt).toLocaleString('pt-BR')}</li>
                )}
                {diagnostics.lastReconnectReason && <li>Ultimo motivo agendado: {diagnostics.lastReconnectReason}</li>}
                {diagnostics.disconnectReason && <li>Motivo da queda: {diagnostics.disconnectReason}</li>}
                {diagnostics.lastDisconnectedAt && (
                  <li>Desconectado em: {new Date(diagnostics.lastDisconnectedAt).toLocaleString('pt-BR')}</li>
                )}
                {diagnostics.lastErrorAt && (
                  <li>Ultimo erro: {new Date(diagnostics.lastErrorAt).toLocaleString('pt-BR')}</li>
                )}
                {diagnostics.connectingSince && (
                  <li>Conectando desde: {new Date(diagnostics.connectingSince).toLocaleString('pt-BR')}</li>
                )}
                {diagnostics.lastConnectedAt && (
                  <li>Ultima conexao OK: {new Date(diagnostics.lastConnectedAt).toLocaleString('pt-BR')}</li>
                )}
              </ul>
            </div>
          )}

          <div className="connection-actions">
            {(status === 'disconnected' || status === 'error') && (
              <button
                onClick={handleInitialize}
                className="btn btn-primary"
                disabled={initializing}
              >
                {initializing ? 'Inicializando...' : '🔗 Inicializar WhatsApp'}
              </button>
            )}
            <button
              onClick={handleRestart}
              className="btn btn-secondary"
              disabled={restarting}
            >
              {restarting ? 'Reiniciando...' : '🔄 Reiniciar Conexao'}
            </button>
            <button
              type="button"
              onClick={handleClearSession}
              className="btn btn-secondary"
              disabled={clearingSession || restarting || initializing}
              title="Apaga a sessao no servidor e forca novo QR"
            >
              {clearingSession ? 'Limpando...' : '🗑️ Limpar sessao (novo QR)'}
            </button>
          </div>

          {status === 'connecting' && (
            <div className="qr-container">
              {diagnostics.authenticated && !qrCode ? (
                <>
                  <p>QR Code lido. Estabelecendo conexao com o WhatsApp...</p>
                  <p className="qr-note">
                    Aguarde o WhatsApp terminar de sincronizar. Não reinicie e não limpe a sessão agora.
                  </p>
                </>
              ) : (
                <>
                  <p>Escaneie o QR Code abaixo com o seu WhatsApp:</p>
                  {qrCode ? (
                    <div className="qr-image-container">
                      <img src={qrCode} alt="QR Code WhatsApp" className="qr-image" />
                    </div>
                  ) : (
                    <div className="qr-instructions">
                      <ol>
                        <li>Abra o WhatsApp no seu celular</li>
                        <li>Toque em Menu (tres pontos) ou Configuracoes</li>
                        <li>Toque em Aparelhos conectados</li>
                        <li>Toque em Conectar um aparelho</li>
                        <li>Escaneie o QR Code abaixo</li>
                      </ol>
                      <p className="qr-note">O QR Code sera gerado automaticamente. Aguarde...</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {status === 'connected' && (
            <div className="connected-info">
              <p className="success-message">✅ WhatsApp conectado com sucesso!</p>
              <p>As notificacoes serao enviadas automaticamente para os vendedores e backoffice.</p>
            </div>
          )}
        </div>

        {/* Mensagem de Teste */}
        <div className="config-card">
          <h2>Enviar Mensagem de Teste</h2>
          <div className="test-form">
            <div className="form-group">
              <label>Telefone (DDD + numero)</label>
              <input
                type="text"
                placeholder="11999999999"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, ''))}
                maxLength="11"
              />
              <small>Apenas numeros, sem DDI (ex: 11999999999)</small>
            </div>
            <div className="form-group">
              <label>Mensagem</label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows="4"
                placeholder="Digite a mensagem de teste..."
              />
            </div>
            <button
              onClick={handleSendTest}
              className="btn btn-primary"
              disabled={sendingTest || status !== 'connected'}
            >
              {sendingTest ? 'Enviando...' : '📤 Enviar Teste'}
            </button>
            {status !== 'connected' && (
              <p className="warning">Conecte o WhatsApp primeiro para enviar mensagens.</p>
            )}
          </div>
        </div>

        {/* Templates de Mensagens */}
        <div className="config-card">
          <h2>Templates de Mensagens</h2>
          <p className="templates-description">
            Os templates sao usados para enviar notificacoes automaticas quando o status das vendas muda.
            Use variaveis como {'{vendedor_nome}'}, {'{cliente_nome}'}, etc.
          </p>

          <div className="templates-list">
            {templates.map((template) => (
              <div key={template.id} className="template-item">
                <div className="template-header">
                  <h3>{getEventTypeName(template.event_type)}</h3>
                  <div className="template-actions">
                    <span className={`status-badge ${template.is_active ? 'status-connected' : 'status-disconnected'}`}>
                      {template.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="btn btn-small btn-secondary"
                    >
                      Editar
                    </button>
                  </div>
                </div>
                <div className="template-variables">
                  <strong>Variaveis:</strong> {getAvailableVariables(template.event_type).join(', ')}
                </div>
                <pre className="template-preview">{template.template}</pre>
              </div>
            ))}
          </div>
        </div>

        {/* Modal de Edicao de Template */}
        {editingTemplate && (
          <div className="modal-overlay" onClick={() => setEditingTemplate(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Editar Template: {getEventTypeName(editingTemplate.event_type)}</h2>
                <span className="close-modal" onClick={() => setEditingTemplate(null)}>×</span>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome do Template</label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Conteudo do Template</label>
                  <textarea
                    value={editingTemplate.template}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, template: e.target.value })}
                    rows="10"
                  />
                </div>
                <div className="form-group">
                  <label>Variaveis Disponiveis</label>
                  <div className="variables-list">
                    {getAvailableVariables(editingTemplate.event_type).map((v) => (
                      <code
                        key={v}
                        onClick={() => {
                          const textarea = document.querySelector('textarea');
                          if (textarea) {
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = editingTemplate.template;
                            const before = text.substring(0, start);
                            const after = text.substring(end);
                            setEditingTemplate({
                              ...editingTemplate,
                              template: before + `{${v}}` + after
                            });
                          }
                        }}
                        className="variable-tag"
                      >
                        {`{${v}}`}
                      </code>
                    ))}
                  </div>
                  <small>Clique em uma variavel para inseri-la no template</small>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingTemplate.is_active}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                    />
                    <span>Template Ativo</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={() => setEditingTemplate(null)} className="btn btn-secondary">
                  Cancelar
                </button>
                <button onClick={handleSaveTemplate} className="btn btn-primary">
                  💾 Salvar Template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WhatsAppConfig;